<template>
  <div
    ref="multiSelectContainer"
    class="ra-multi-select"
    @keydown.esc="showDropdown = false"
    @focusout="handleDropdownFocusout"
  >
    <div class="ra-multi-select__input" @pointerdown="showDropdown = !showDropdown">
      <div class="ra-multi-select__tags">
        <span v-for="(item, index) in selectedItems" :key="index" class="ra-multi-select__tag">
          <span class="ra-multi-select__tag-text">{{ getItemLabel(item) }}</span>
          <span
            tabindex="0"
            role="button"
            class="ra-multi-select__tag-remove"
            @click.stop="removeItem(index)"
            @keydown.space.prevent="removeItem(index)"
            @keydown.enter="removeItem(index)"
          >
            <svg
              aria-hidden="true"
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="currentColor"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M7.9998 6.94043L1.78887 0.729492L0.729492 1.78887L6.94043 7.9998L0.729492 14.2107L1.78887 15.2701L7.9998 9.05918L14.2107 15.2701L15.2701 14.2107L9.05918 7.9998L15.2701 1.78887L14.2107 0.729492L7.9998 6.94043Z"
              />
            </svg>
          </span>
        </span>
        <input
          :id="inputId"
          type="text"
          role="combobox"
          class="ra-multi-select__field"
          :placeholder="placeholder"
          readonly
          :aria-expanded="showDropdown ? 'true' : 'false'"
          aria-haspopup="listbox"
          aria-controls="multi-select__options"
          :aria-labelledby="labelId"
          @keydown.enter="showDropdown = true"
          @keydown.space.prevent="showDropdown = true"
          @keydown.esc="showDropdown = false"
        />
      </div>
    </div>

    <div v-if="showDropdown" class="ra-multi-select__dropdown">
      <ul
        id="multi-select__options"
        class="ra-multi-select__options"
        role="listbox"
        :aria-labelledby="labelId"
      >
        <li
          v-for="(item, index) in filteredOptions"
          :key="index"
          role="option"
          tabindex="0"
          :aria-selected="isSelected(item)"
          :class="[
            'ra-multi-select__option',
            {
              'ra-multi-select__option--selected': isSelected(item),
            },
          ]"
          @click="toggleItemSelection(item)"
          @keydown.space.prevent="toggleItemSelection(item)"
          @keydown.enter.prevent="toggleItemSelection(item)"
        >
          <div class="ra-multi-select__option-button">
            <span class="ra-multi-select__option-label">{{ getItemLabel(item) }}</span>
            <span v-if="isSelected(item)" class="ra-multi-select__option-checkmark">✓</span>
          </div>
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
const INPUT_CHANGE_EVENT = 'input-changed';
import { common as commonProps } from './prop-definitions.js';

export default {
  name: 'refapp-multi-select',
  props: {
    ...commonProps,
  },
  emits: [INPUT_CHANGE_EVENT],
  data() {
    return {
      showDropdown: false,
      selectedItems: [...this.value],
      filteredOptions: this.keyValueFormat ? Object.keys(this.options) : [...this.options],
    };
  },
  computed: {
    rawSelectedItems() {
      return this.selectedItems.map((item) => {
        return item[this.itemValue] || item;
      });
    },
  },
  watch: {
    value(newVal) {
      this.selectedItems = [...newVal];
    },
  },
  mounted() {
    document.addEventListener('click', this.handleClickOutside);
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleClickOutside);
  },
  unmounted() {
    document.removeEventListener('click', this.handleClickOutside);
  },
  methods: {
    handleDropdownFocusout(event) {
      if (!this.$refs.multiSelectContainer?.contains(event.relatedTarget)) {
        this.showDropdown = false;
      }
    },
    handleClickOutside(event) {
      if (!this.$refs.multiSelectContainer?.contains(event.target)) {
        this.showDropdown = false;
      }
    },
    toggleItemSelection(item) {
      const index = this.selectedItems.findIndex(
        (selected) => this.getItemValue(selected) === this.getItemValue(item),
      );
      if (index === -1) {
        this.selectedItems.push(item);
      } else {
        this.selectedItems.splice(index, 1);
      }
      this.confirmSelection();
    },
    isSelected(item) {
      return this.selectedItems.some(
        (selected) => this.getItemValue(selected) === this.getItemValue(item),
      );
    },
    confirmSelection() {
      this.$emit(INPUT_CHANGE_EVENT, this.rawSelectedItems);
    },
    removeItem(index) {
      this.showDropdown = false;
      this.selectedItems.splice(index, 1);
      this.$emit(INPUT_CHANGE_EVENT, this.rawSelectedItems);
      this.$refs.multiSelectContainer?.focus();
    },
    getItemLabel(item) {
      if (this.keyValueFormat) {
        return typeof item === 'string' ? this.options[item] : item;
      }
      return typeof item === 'object' ? item[this.itemLabel] : item;
    },
    getItemValue(item) {
      if (this.keyValueFormat) {
        return item;
      }
      return typeof item === 'object' ? item[this.itemValue] : item;
    },
  },
};
</script>

<style lang="scss">
@use './styles/style.scss';
</style>
